/**
 * Copyright (C) 2018-present, Botobag Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *      http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import assert from 'assert';

import { Decimal } from 'decimal.js';

import draftOrders from './data/draftOrders';

import { fetchAddress } from './address';
import { fetchCustomer } from './customer';
import { id } from './data/id';
import { fetchEdges } from './edge';
import { fetchFulfillmentServiceData } from './fulfillmentService';
import { fetchProductForVariant } from './product';

import { DraftOrderInput } from '@/api/draftOrder';
import { assertExists } from '@/lib/assert';
import { ZERO } from '@/lib/money';
import {
  Address,
  CustomerData,
  DraftFulfillmentData,
  DraftFulfillmentOptionData,
  DraftOrderData,
  DraftOrderItemData,
  EdgeType,
  FulfillmentServiceData,
  FulfillmentServiceType,
  ProductVariantData,
  StoreAddress,
} from '@/models';

function validatedQuantity(quantity: any): number {
  // Make sure the given quantity is a number.
  const q = parseInt(quantity, 10);
  if (isNaN(q)) {
    throw new Error(`provided quantity is not a number: ${quantity}`);
  } else if (q < 0) {
    throw new Error(`provided quantity must not be negative: ${quantity}`);
  }
  return q;
}

function computeFulfillmentPrice(fulfillment: DraftFulfillmentData) {
  // TODO: Currently the computation is based on fulfillment service type. This woule be changed in
  //       the future by using parameters provided by the service for calculation.
  const price = fulfillment.price;
  switch (fulfillment.service.handle) {
    case FulfillmentServiceType.InStorePickup:
      price.shipping = '0';
      break;
    case FulfillmentServiceType.SevenEleven:
      price.shipping = '60';
      break;
    case FulfillmentServiceType.Ecan:
      price.shipping = '100';
      break;
    case FulfillmentServiceType.MajorApplianceDelivery:
      price.shipping = (fulfillment.items.length * 500).toString();
      break;
    case FulfillmentServiceType.KitchenApplianceDelivery:
      price.shipping = (fulfillment.items.length * 1000).toString();
      break;
    case FulfillmentServiceType.WestinghouseTVDelivery:
      price.shipping = (fulfillment.items.length * 1000).toString();
      break;
    case FulfillmentServiceType.FruitDirectShipping:
      price.shipping = '0';
      break;
    case FulfillmentServiceType.TravelToBuyDelivery:
      price.shipping = '0';
      break;
    default:
      throw new Error(`unsupported fulfillment service type: ${fulfillment.service.handle}`);
  }
}

async function fetchFulfillmentShippingAddressOptions(
  fulfillment: DraftFulfillmentData,
  customer?: CustomerData): Promise<Address[] | StoreAddress[] | undefined> {
  switch (fulfillment.service.handle) {
    case FulfillmentServiceType.InStorePickup:
      // Not applicable
      return;
    case FulfillmentServiceType.SevenEleven:
      // Fetch customer's stored address for 7-11.
      if (customer) {
        return fetchEdges<StoreAddress[]>(
          EdgeType.CustomerAddressesForFulfillmentService,
          fulfillment.service.id,
          customer.id).then((edges) => {
            // FIXME: Should limit to one edge.
            return edges.reduce<StoreAddress[]>(
              (prev, edge) => (prev.concat(edge.data)), []);
            // if (!fulfillment.shippingAddress && fulfillment.shippingAddressOptions.length > 0) {
            //   fulfillment.shippingAddress = fulfillment.shippingAddressOptions[0];
            // }
            // return;
          });
      }
      break;
    case FulfillmentServiceType.Ecan:
    case FulfillmentServiceType.MajorApplianceDelivery:
    case FulfillmentServiceType.KitchenApplianceDelivery:
    case FulfillmentServiceType.WestinghouseTVDelivery:
    case FulfillmentServiceType.FruitDirectShipping:
    case FulfillmentServiceType.TravelToBuyDelivery:
      if (customer) {
        return customer.addresses;
        // if (!fulfillment.shippingAddress && customer.addresses.length > 0) {
        //   // Select the first address as default address.
        //   fulfillment.shippingAddress = customer.addresses[0];
        // }
      }
      break;
    default:
      throw new Error(`unsupported fulfillment service type: ${fulfillment.service.handle}`);
  }
}

export async function fetchDraftOrder(orderId: string): Promise<DraftOrderData> {
  return updateDraftOrder(orderId, {});
}

export async function createDraftOrder(orderInput: DraftOrderInput): Promise<DraftOrderData> {
  // Fetch customer.
  const customer = await fetchCustomer();

  // Create a new empty draft order.
  const order: DraftOrderData = {
    id: id(),
    currency: orderInput.currency,
    customer: (customer) ? customer : undefined,
    items: [],
    fulfillments: [],
    availablePaymenMethods: [],
    price: {
      principal: '0',
    },
  };
  draftOrders.push(order);

  return updateDraftOrder(order.id, orderInput);
}

// draftOrderInputFromCart creates a DraftOrderInput which retains current states of the order in
// the input if there's no change made to the corresponding state.
export function draftOrderInputFromOrder(order: DraftOrderData, changes: Partial<DraftOrderInput>): DraftOrderInput {
  return {
    currency: (changes.currency) ? changes.currency : order.currency,

    items: (changes.items) ? changes.items : order.items.map((item) => ({
      id: item.id,
      variantId: item.variant.id,
      quantity: item.quantity,
    })),

    fulfillments: (changes.fulfillments) ? changes.fulfillments : order.fulfillments.map((fulfillment) => ({
      serviceId: fulfillment.service.id,
      addressId: (fulfillment.shippingAddress) ? fulfillment.shippingAddress.id : undefined,
      itemIds: fulfillment.items.map((item) => (item.id)),
    })),
  };
}

export async function updateDraftOrder(orderId: string, changes: Partial<DraftOrderInput>): Promise<DraftOrderData> {
  // Find the current order.
  const curOrder = draftOrders.find((draftOrder) => (draftOrder.id === orderId));
  if (!curOrder) {
    throw new Error(`cannot find draft order with given id: ${orderId}`);
  }

  const orderInput = draftOrderInputFromOrder(curOrder, changes);

  // Load items in new order.
  const orderItems =
    Promise.all(orderInput.items
      .filter((item) => (item.quantity > 0))
      .map((item) => (fetchProductForVariant(item.variantId))))
      .then<DraftOrderItemData[]>((products) => (products.map((product, index) => {
        const inputItem = orderInput.items[index];
        return {
          id: (inputItem.id) ? inputItem.id : id(),
          variant: {
            id: inputItem.variantId,
            product,
          },
          // Fix later.
          price: {
            principal: '0',
          },
          quantity: inputItem.quantity,
        };
      })));

  // Load fulfillment in new order.
  const orderFulfillment =
      Promise.all(orderInput.fulfillments.map((fulfillment) => (
        Promise.all([
          fetchFulfillmentServiceData(fulfillment.serviceId),
          (fulfillment.addressId) ? fetchAddress(fulfillment.addressId) : Promise.resolve(undefined),
        ]))))
        .then<DraftFulfillmentData[]>((servicesAndAddresses) => (
          servicesAndAddresses.map((serviceAndAddress) => ({
            service: serviceAndAddress[0],
            // Fix later.
            items: [],
            shippingAddress: (serviceAndAddress.length > 1) ? serviceAndAddress[1] : undefined,
            // Fix later.
            price: {},
          }))));

  const order: DraftOrderData = {
    id: orderId,
    // Apply currency change from input.
    currency: orderInput.currency,
    customer: curOrder.customer,
    items: await orderItems,
    fulfillments: await orderFulfillment,
    billingAddress: curOrder.billingAddress,
    price: {
      principal: '0',
    },
    availablePaymenMethods: [],
  };

  // itemVariantMap maps item to its product variant.
  const itemVariantMap = new Map<DraftOrderItemData, ProductVariantData>();
  for (const item of order.items) {
    // Currently, item price is as simple as copying the variant price.
    const product = item.variant.product;
    const variant = assertExists(product.variants.find((v) => (v.id === item.variant.id)));
    itemVariantMap.set(item, variant);
  }

  // itemFulfillmentMap maps item to its fulfillment.
  const itemFulfillmentMap = new Map<DraftOrderItemData, DraftFulfillmentData>();

  // Scan orderInput.fulfillment[i].items to initialize itemFulfillmentMap.
  order.fulfillments.forEach((fulfillment, index) => {
    for (const inputItem of orderInput.fulfillments[index].itemIds) {
      // Skip inputItem that is not listed in order items.
      const item = order.items.find((orderItem) => (orderItem.id === inputItem));
      if (!item) {
        continue;
      }

      // Throw an error if the inputItem has been assigned to a fulfillment.
      const prevFulfillment = itemFulfillmentMap.get(item);
      if (prevFulfillment) {
        throw new Error(`Item ${item.variant.product.name} can only be fulfilled with one ` +
        `fulfillment but both of ${prevFulfillment.service.name} and ${fulfillment.service.name} ` +
        `is assigned.`);
      }

      // Make sure the "fulfillment" is listed as one of available fulfillment methods in "item".
      const variant = itemVariantMap.get(item)!;
      if (!variant.fulfillments) {
        // The item cannot be fulfilled with any methods. Skip now and let the following loop on
        // order items to handle it.
        continue;
      } else if (!variant.fulfillments.some((f) => (f.id === fulfillment.service.id))) {
        throw new Error(`Item ${item.variant.product.name} cannot be fulfilled with ` +
          `${fulfillment.service.name}`);
      }

      itemFulfillmentMap.set(item, fulfillment);

      // Add item to fulfillment.items.
      fulfillment.items.push(item);
    }
  });

  // Remove the fulfillments without any items.
  order.fulfillments = order.fulfillments.filter((fulfillment) => (fulfillment.items.length > 0));

  const fulfillmentMap = new Map</* FulfillmentService.id */string, {
    // How many items in the order can be fulfilled by the fulfillment service.
    items: Set<DraftOrderItemData>;

    // The shipping address that has been stored by customer for this fulfillment service.
    addresses?: Address[];

    service: FulfillmentServiceData;
    fulfillment?: DraftFulfillmentData;
  }>();

  // Compute principal price and available fulfillments for each item.
  for (const item of order.items) {
    // Currently, item price is as simple as copying the variant price.
    const product = item.variant.product;
    const variant = itemVariantMap.get(item)!;
    const variantPrice = (variant.price) ? new Decimal(variant.price) : ZERO;

    if (product.priceCurrency !== order.currency) {
      // TODO: Use currency converter to convert product price into order's currency.
      throw new Error(`product currency (${product.priceCurrency}) differs from order currency ` +
      `(${order.currency}) which is unable to handle now.`);
    }
    item.price.principal = variantPrice.mul(item.quantity).toString();

    if (!variant.fulfillments) {
      throw new Error(`Item ${product.name} is unable to be fulfilled`);
    }

    variant.fulfillments.forEach((fulfillment) => {
      // Setup fulfillmentMap.
      const fulfillmentInfo = fulfillmentMap.get(fulfillment.id);
      if (fulfillmentInfo) {
        fulfillmentInfo.items.add(item);
      } else {
        fulfillmentMap.set(fulfillment.id, {
          items: new Set([item]),
          service: fulfillment,
        });
      }
    });
  }

  // Set "fulfillment" in each entry of fulfillmentMap.
  order.fulfillments.forEach((fulfillment) =>
    (fulfillmentMap.get(fulfillment.service.id)!.fulfillment = fulfillment));

  // Try to select one of the fulfillment in current order for the items that haven't been assigned
  // one.
  for (const item of order.items) {
    if (itemFulfillmentMap.has(item)) {
      continue;
    }

    const variant = itemVariantMap.get(item)!;
    const itemFulfillment = variant.fulfillments!.reduce<DraftFulfillmentData | undefined>(
      (prev, fulfillment) => {
        const info = fulfillmentMap.get(fulfillment.id)!;
        if (!info.fulfillment) {
          // At this point, if info.fulfillment is not set, it means the fulfillment is not
          // assigned to any item by the user. Skip it.
          return prev;
        }

        // Replace prev if the fulfillment is assigned with more items. This tries to put as many
        // items as possible in a fulfillment.
        if (!prev ||  prev.items.length < info.fulfillment.items.length) {
          return info.fulfillment;
        } else {
          return prev;
        }
      }, undefined);

    if (itemFulfillment) {
      itemFulfillment.items.push(item);
      itemFulfillmentMap.set(item, itemFulfillment);
    }
  }

  // Select fulfillment for items that have not been assigned to any fulfillment.
  const doesEveryItemHasFulfillment = (itemFulfillmentMap.size === order.items.length);
  if (!doesEveryItemHasFulfillment) {
    // Sort fulfillment services by their availability counts.
    const sortedFulfillmentService = Array.from(fulfillmentMap.keys());
    sortedFulfillmentService.sort((a, b) =>
      (fulfillmentMap.get(b)!.items.size - fulfillmentMap.get(a)!.items.size));

    for (const serviceId of sortedFulfillmentService) {
      const fulfillmentInfo = fulfillmentMap.get(serviceId)!;
      for (const item of fulfillmentInfo.items) {
        if (itemFulfillmentMap.has(item)) {
          continue;
        }

        if (!fulfillmentInfo.fulfillment) {
          fulfillmentInfo.fulfillment = {
            service: fulfillmentInfo.service,
            items: [item],
            price: {},
          };
          order.fulfillments.push(fulfillmentInfo.fulfillment);
        }

        itemFulfillmentMap.set(item, fulfillmentInfo.fulfillment);
      }

      if (itemFulfillmentMap.size === order.items.length) {
        break;
      }
    }
  }

  // Compute shipping rates.
  order.fulfillments.forEach((fulfillment) => computeFulfillmentPrice(fulfillment));
  // Compute shipping address.
  await Promise.all(order.fulfillments.map(
    (fulfillment) => (
      fetchFulfillmentShippingAddressOptions(fulfillment, order.customer)
        .then((addresses) => {
          fulfillment.shippingAddressOptions = addresses;
          if (!fulfillment.shippingAddress && addresses && addresses.length > 0) {
            fulfillment.shippingAddress = addresses[0];
          }
        }))));

  // Compute alternative fulfillments.
  await Promise.all(order.fulfillments.map((fulfillment) => {
    const fulfillmentOptions: Array<Promise<DraftFulfillmentOptionData>> = [];
    for (const fulfillmentInfo of fulfillmentMap.values()) {
      let fulfillmentOption: Promise<DraftFulfillmentOptionData>;
      if (fulfillmentInfo.fulfillment === fulfillment) {
        fulfillmentOption = Promise.resolve({
          service: fulfillment.service,
          addresses: fulfillment.shippingAddressOptions,
          shippingPrice: fulfillment.price.shipping,
        });
      } else {
        if (!fulfillment.items.every((item) => (fulfillmentInfo.items.has(item)))) {
          continue;
        }

        // Create a DraftFulfillmentData to be sent to computeFulfillmentPrice and
        // fetchFulfillmentShippingAddressOptions to get shipping price and address option.
        const f: DraftFulfillmentData = {
          service: fulfillmentInfo.service,
          items: fulfillment.items,
          price: {},
        };

        computeFulfillmentPrice(f);

        fulfillmentOption = fetchFulfillmentShippingAddressOptions(f, order.customer)
          .then((addresses) => ({
            service: f.service,
            addresses,
            shippingPrice: f.price.shipping,
          }));
      }
      fulfillmentOptions.push(fulfillmentOption);
    }

    return Promise.all(fulfillmentOptions)
      .then((options) => {
        fulfillment.fulfillmentOptions = options;
      });
  }));

  // Compute order price. Currently, it simply sums up all item and shipping prices.
  order.price.principal = order.items.reduce<Decimal>(
    (prev, cur) => (prev.add(cur.price.principal)), ZERO).toString();
  order.price.shipping = order.fulfillments.reduce<Decimal>(
    (prev, cur) => (prev.add(cur.price.shipping!)), ZERO).toString();

  // Compute order payment methods.

  // Write back the order.
  const draftOrderIdx = draftOrders.findIndex((draftOrder) => (draftOrder.id === order.id));
  assert.ok(draftOrderIdx >= 0, `no such draft order: ${order.id}`);
  draftOrders[draftOrderIdx] = order;

  // Return.
  return order;
}
